import { camelcaseKeys } from 'src/utils/camelize-keys';
import { decamelizeKeys } from 'src/utils/decamelize-keys';
import {
  dateToTimestamp,
  removeReadOnlyFields,
  timestampToDate,
} from 'src/utils/fieldNormalizers';

import { InventoryAdjustment, Product, ProductVariant } from './_types';
import { getAdjustmentType } from './utils';

const readOnlyFields = [
  'created_at',
  'updated_at',
  'image_url',
  'thumbnail',
  'large_thumbnail',
  'product_variants',
];

const productVariantReadOnlyFields = [
  'created_at',
  'updated_at',
  'display_name',
  'image_url',
  'inventory_levels',
  'payment_options',
  'name',
];

const inventoryLevelReadOnlyFields = ['created_at', 'updated_at'];

const inventoryAdjustmentReadOnlyFields = [
  'id',
  'created_at',
  'updated_at',
  'adjusted_by_id',
  'adjusted_by_type',
  'adjusted_by_name',
  'adjusted_by_email',
  'quantity_before',
  'quantity_after',
  'product_variant',
  'cart_item_id',
];

// Use TypeScript's type annotation to enforce the type of the input parameter
// and the return type of the function.
export function normalizeProduct(prod: any): Product {
  const originalOptionValues = (prod.product_variants || []).map(
    (variant) => variant.option_values
  );

  const product = camelcaseKeys(prod, { deep: true });

  product.createdAt = timestampToDate(product.createdAt);

  if (product.productVariants && originalOptionValues.length > 0) {
    product.productVariants.forEach((variant: any, index: number) => {
      variant.optionValues = originalOptionValues[index];
    });
  }

  return product as Product;
}

export function denormalizeProduct(prod: Partial<Product>): any {
  // Save original optionValues before decamelization to preserve user-entered option names
  const originalOptionValues = (prod.productVariants || []).map(
    (variant) => variant.optionValues
  );

  const product = decamelizeKeys(prod, { deep: true });

  product.product_variants_attributes = (product.product_variants || []).map(
    (variant: any, index: number) => {
      const normalizedVariant = {
        ...variant,
        // Restore original optionValues to preserve user-entered option names
        option_values: originalOptionValues[index] || variant.option_values,
        inventory_levels_attributes: (variant.inventory_levels || []).map(
          (level: any) => {
            const normalizedLevel = {
              ...level,
            };

            removeReadOnlyFields(normalizedLevel, inventoryLevelReadOnlyFields);
            if (
              String(normalizedLevel.id).includes('temp-variant') ||
              String(normalizedLevel.id).includes('temp-default')
            ) {
              delete normalizedLevel.id;
            }

            if (normalizedLevel.destroy) {
              normalizedLevel._destroy = 1;
              delete normalizedLevel.destroy;
            }

            return normalizedLevel;
          }
        ),
      };

      removeReadOnlyFields(normalizedVariant, productVariantReadOnlyFields);

      if (
        String(normalizedVariant.id).includes('temp-variant') ||
        String(normalizedVariant.id).includes('temp-default')
      ) {
        delete normalizedVariant.id;
      }

      if (normalizedVariant.destroy) {
        normalizedVariant._destroy = 1;
        delete normalizedVariant.destroy;
      }

      return normalizedVariant;
    }
  );

  product.created_at = dateToTimestamp(product.created_at);
  removeReadOnlyFields(product, readOnlyFields);

  return product as Partial<Product>;
}

export function normalizeProductVariant(variant: any): ProductVariant {
  return camelcaseKeys(variant, { deep: true }) as ProductVariant;
}

export function normalizeInventoryAdjustment(
  adjustment: any
): InventoryAdjustment {
  const normalizedAdjustment = camelcaseKeys(adjustment, { deep: true });

  normalizedAdjustment.createdAt = timestampToDate(
    normalizedAdjustment.createdAt
  );
  normalizedAdjustment.updatedAt = timestampToDate(
    normalizedAdjustment.updatedAt
  );

  return normalizedAdjustment as InventoryAdjustment;
}

export function denormalizeInventoryAdjustment(
  adjustment: Partial<InventoryAdjustment>
): any {
  const denormalizedAdjustment = decamelizeKeys(adjustment, { deep: true });

  denormalizedAdjustment.created_at = dateToTimestamp(
    denormalizedAdjustment.created_at
  );
  denormalizedAdjustment.updated_at = dateToTimestamp(
    denormalizedAdjustment.updated_at
  );

  denormalizedAdjustment.adjustment_type = getAdjustmentType(
    denormalizedAdjustment.reason
  );

  removeReadOnlyFields(
    denormalizedAdjustment,
    inventoryAdjustmentReadOnlyFields
  );

  return denormalizedAdjustment;
}
